import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Chip,
  Paper,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { BlocklistItem } from "../../api/moderation";
import { useModerationStore } from "../../store/moderationStore";
import { useModerationApi } from "./hooks/useModerationApi";
import { useGlobalLoading } from "../../hooks/useGlobalLoading";
import { useSnackbar } from "../../hooks/useSnackbar";

interface BlocklistSectionProps {
  onRefresh: () => void;
}

export const BlocklistSection: React.FC<BlocklistSectionProps> = ({
  onRefresh,
}) => {
  const api = useModerationApi();
  const { withLoading } = useGlobalLoading();
  const { blocklist, loadBlocklist } = useModerationStore();

  const [newItemType, setNewItemType] = useState<
    "emojis" | "emoticons" | "words"
  >("words");
  const [newItemValue, setNewItemValue] = useState("");
  const { showSnackbar, hideSnackbar, snackbar } = useSnackbar();

  const handleAddItem = async () => {
    if (!newItemValue.trim()) {
      showSnackbar("Please enter a value", "error");
      return;
    }

    try {
      const item: BlocklistItem = {
        type: newItemType,
        value: newItemValue.trim(),
      };

      await withLoading(api.addBlocklistItem(item));
      await loadBlocklist();
      showSnackbar("Item added to blocklist", "success");
      setNewItemValue("");
    } catch (error: any) {
      showSnackbar(error.message || "Failed to add item", "error");
    }
  };

  const handleDeleteItem = async (
    type: "emojis" | "emoticons" | "words",
    value: string
  ) => {
    try {
      const item: BlocklistItem = { type, value };
      await withLoading(api.deleteBlocklistItem(item));
      await loadBlocklist();
      showSnackbar("Item removed from blocklist", "success");
    } catch (error: any) {
      showSnackbar(error.message || "Failed to remove item", "error");
    }
  };

  const renderItemList = (
    type: "emojis" | "emoticons" | "words",
    items: string[]
  ) => {
    return (
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography
          variant="subtitle1"
          sx={{ mb: 2, textTransform: "capitalize" }}
        >
          {type}
        </Typography>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {items.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No items in {type} blocklist
            </Typography>
          ) : (
            items.map((item) => (
              <Chip
                key={item}
                label={item}
                onDelete={() => handleDeleteItem(type, item)}
                deleteIcon={<DeleteIcon />}
                sx={{ fontSize: "0.9rem" }}
              />
            ))
          )}
        </Box>
      </Paper>
    );
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Blocklist Management
      </Typography>

      {snackbar.open && (
        <Alert
          severity={snackbar.severity}
          sx={{ mb: 2 }}
          onClose={hideSnackbar}
        >
          {snackbar.message}
        </Alert>
      )}

      {/* Add new item */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          Add Item to Blocklist
        </Typography>

        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 3 }}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={newItemType}
                onChange={(e) => setNewItemType(e.target.value as any)}
                label="Type"
              >
                <MenuItem value="emojis">Emojis</MenuItem>
                <MenuItem value="emoticons">Emoticons</MenuItem>
                <MenuItem value="words">Words</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, sm: 7 }}>
            <TextField
              fullWidth
              label="Value"
              value={newItemValue}
              onChange={(e) => setNewItemValue(e.target.value)}
              placeholder={`Enter ${newItemType.slice(0, -1)} to block`}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  handleAddItem();
                }
              }}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 2 }}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddItem}
              sx={{
                height: "56px",
                backgroundColor: "#7357FF",
                "&:hover": { backgroundColor: "#5a42cc" },
              }}
            >
              Add
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Display blocklists */}
      {renderItemList("emojis", blocklist?.emojis || [])}
      {renderItemList("emoticons", blocklist?.emoticons || [])}
      {renderItemList("words", blocklist?.words || [])}
    </Box>
  );
};
